import { OfferingMenuFeatures, OfferingMenuPermissions, SelectionCategory } from 'modules/shared-menu/types';
import { getTypedKeys } from 'modules/shared-menu/utils';

import { SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET } from './components/DataTable/constants';
import { SheetNameToShapedLinesMap, WithRowNumber } from './libs/googleSheets';
import { ShapedLine } from './libs/googleSheets/models';
import { OptionLine, OfferLine, CategoryLine, ValidationError } from './libs/googleSheets/types';
import { Offer } from '../SharedMenuEditorPage/types';
import { flattenCategories } from '../SharedMenuEditorPage/utils';

export const isOptionLineType = (object: Partial<ShapedLine>): object is Partial<OptionLine> =>
  Object.prototype.hasOwnProperty.call(object, 'option_value_id');

/**
 * removes empty option lines that separate option groups in the spreadsheet
 * we're not displaying them on the frontend
 */
export const getOptionsWithoutEmptyLines = (options: WithRowNumber<Partial<ShapedLine>>[]) =>
  options.filter(rowObject => !(Object.keys(rowObject).length === 1 && 'rowNumber' in rowObject));

const getErrorsDiff = (errors: ValidationError[] = [], allItems: Partial<ShapedLine>[] = []) =>
  errors.reduce(
    (acc, error) => {
      const currentItem = allItems[error.line];
      const rowHasError = !!currentItem;

      if (!rowHasError) {
        return acc;
      }

      const isUpdateError = 'id' in currentItem || 'option_value_id' in currentItem;

      return isUpdateError
        ? { ...acc, updateErrors: acc.updateErrors + 1 }
        : { ...acc, additionErrors: acc.additionErrors + 1 };
    },
    { additionErrors: 0, updateErrors: 0 },
  );

type GetSummaryInfoArgs = {
  offers: {
    data?: WithRowNumber<Partial<OfferLine>>[];
    errors?: ValidationError[];
  };
  options: {
    data?: WithRowNumber<Partial<OptionLine>>[];
    errors?: ValidationError[];
  };
  categories: {
    data?: WithRowNumber<Partial<CategoryLine>>[];
    errors?: ValidationError[];
  };
  isDownloadingAndValidating: boolean;
};

const EMPTY_INPUT = {
  data: [],
  errors: [],
};

const EMPTY_SUMMARY = {
  numberOfAdditions: 0,
  numberOfUpdates: 0,
  numberOfErrors: 0,
};

export const getSummaryInfo = ({
  offers = EMPTY_INPUT,
  options = EMPTY_INPUT,
  categories = EMPTY_INPUT,
  isDownloadingAndValidating,
}: GetSummaryInfoArgs) => {
  if (isDownloadingAndValidating) {
    return EMPTY_SUMMARY;
  }

  const optionsWithoutEmptyLines = getOptionsWithoutEmptyLines(options.data ?? []);

  const allOffersToUpdate = (offers.data ?? []).reduce((acc, offer) => (offer?.id ? acc + 1 : acc), 0);

  const allOptionsToUpdate = (options.data ?? []).reduce(
    (acc, option) => ('option_value_id' in option || 'id' in option ? acc + 1 : acc),
    0,
  );

  const allCategoriesToUpdate = (categories.data ?? []).reduce((acc, category) => (category?.id ? acc + 1 : acc), 0);

  const offersErrors = getErrorsDiff(offers.errors, offers.data);
  const optionsErrors = getErrorsDiff(options.errors, options.data);
  const categoriesErrors = getErrorsDiff(categories.errors, categories.data);

  const offersToUpdate = allOffersToUpdate - offersErrors.updateErrors;
  const offersAdditions = (offers.data ?? []).length - allOffersToUpdate - offersErrors.additionErrors;

  const optionsToUpdate = allOptionsToUpdate - optionsErrors.updateErrors;
  const optionsAdditions = optionsWithoutEmptyLines.length - allOptionsToUpdate - optionsErrors.additionErrors;

  const categoriesToUpdate = allCategoriesToUpdate - categoriesErrors.updateErrors;
  const categoriesAdditions = (categories.data ?? []).length - allCategoriesToUpdate - categoriesErrors.additionErrors;

  const numberOfAdditions = offersAdditions + optionsAdditions + categoriesAdditions;
  const numberOfUpdates = offersToUpdate + optionsToUpdate + categoriesToUpdate;
  const numberOfErrors =
    (offers.errors ?? []).length + (options.errors ?? []).length + (categories.errors ?? []).length;

  return {
    numberOfAdditions,
    numberOfUpdates,
    numberOfErrors,
  };
};

export const removeRowNumbers = <T extends Partial<ShapedLine>>(
  rows: WithRowNumber<T>[] = [],
): Omit<WithRowNumber<T>, 'rowNumber'>[] => rows.map(({ rowNumber, ...rest }) => rest);

export const buildErrorsMap = (errorsArray: ValidationError[] = []) => {
  const map = new Map<ValidationError['line'], ValidationError['message']>();

  errorsArray.forEach(item => {
    map.set(item.line, [...item.message]);
  });

  return map;
};

export const buildNonAllowedFields = (
  menuFeatures: OfferingMenuFeatures,
  menuPermissions: OfferingMenuPermissions,
): SheetNameToShapedLinesMap => {
  const offerFieldsAccessRules: Partial<Record<keyof OfferLine, boolean>> = {
    inventory_mode: menuFeatures.isRetail,
    mandatory_warnings: menuFeatures.mandatoryWarnings,
    sell_by_weight_type: menuFeatures.supportWeightedItems,
    weight_in_grams: menuFeatures.supportWeightedItems,
    sell_by_weight_grams_per_step: menuFeatures.supportWeightedItems,
    merchant_sku: menuFeatures.hasCatalogSource || menuFeatures.isRetail,
    gtin: menuFeatures.hasCatalogSource || menuFeatures.isRetail,
    is_bundle_offer: menuFeatures.bundleItems,
    deposit_id: menuFeatures.bottleDeposits,
    external_id: menuPermissions.posIdField,
    vat: menuPermissions.itemVatChange,
    vat_category: menuPermissions.itemVatChange,
  };

  const optionFieldsAccessRules: Partial<Record<keyof OptionLine, boolean>> = {
    deposit_id: menuFeatures.bottleDeposits,
    mandatory_warnings: menuFeatures.mandatoryWarnings,
    vat: menuPermissions.itemVatChange,
    vat_category: menuPermissions.itemVatChange,
  };
  const categoryFieldsAccessRules: Partial<Record<keyof CategoryLine, boolean>> = {
    subcategories: menuFeatures.isRetail,
  };

  return {
    offers: getTypedKeys(offerFieldsAccessRules).filter(key => !offerFieldsAccessRules[key]),
    options: getTypedKeys(optionFieldsAccessRules).filter(key => !optionFieldsAccessRules[key]),
    categories: getTypedKeys(categoryFieldsAccessRules).filter(key => !categoryFieldsAccessRules[key]),
  };
};

type BuildErrorMessagesArgs = {
  lineObj: WithRowNumber<Partial<ShapedLine>>;
  nonAllowedFieldsForEntity: SheetNameToShapedLinesMap[keyof SheetNameToShapedLinesMap];
};
const buildErrorMessages = ({ lineObj, nonAllowedFieldsForEntity }: BuildErrorMessagesArgs): string[] =>
  getTypedKeys(lineObj).reduce((currMessages, key) => {
    if (key === 'rowNumber' || !nonAllowedFieldsForEntity.includes(key) || lineObj[key] === undefined) {
      return currMessages;
    }

    return [...currMessages, `Not allowed for editing: [${key}]`];
  }, []);

type BuildValidationErrorsArgs = {
  lines: WithRowNumber<Partial<ShapedLine>>[];
  nonAllowedFieldsForEntity: SheetNameToShapedLinesMap[keyof SheetNameToShapedLinesMap];
};
const buildValidationErrors = ({ lines, nonAllowedFieldsForEntity }: BuildValidationErrorsArgs) => {
  const errors: ValidationError[] = [];

  lines.forEach(line => {
    const errorMessages = buildErrorMessages({
      lineObj: line,
      nonAllowedFieldsForEntity,
    });

    if (errorMessages.length) {
      errors.push({ line: line.rowNumber - SPREADSHEET_TO_VALIDATION_ARRAY_OFFSET, message: errorMessages });
    }
  });

  return errors;
};

export type BuildPermissionsValidationsArgs = {
  offers: WithRowNumber<Partial<OfferLine>>[];
  options: WithRowNumber<Partial<OptionLine>>[];
  categories: WithRowNumber<Partial<CategoryLine>>[];
  nonAllowedFields: SheetNameToShapedLinesMap;
};

export type PermissionsValidations = {
  offers: ValidationError[];
  options: ValidationError[];
  categories: ValidationError[];
};

export const buildPermissionsValidations = ({
  offers,
  options,
  categories,
  nonAllowedFields,
}: BuildPermissionsValidationsArgs): PermissionsValidations => ({
  offers: buildValidationErrors({ lines: offers, nonAllowedFieldsForEntity: nonAllowedFields.offers }),
  options: buildValidationErrors({ lines: options, nonAllowedFieldsForEntity: nonAllowedFields.options }),
  categories: buildValidationErrors({ lines: categories, nonAllowedFieldsForEntity: nonAllowedFields.categories }),
});

export const mergeErrors = (
  apiValidationErrors: ValidationError[] = [],
  permissionsValidationsErrors: ValidationError[] = [],
) => {
  const errorsMap = new Map<ValidationError['line'], ValidationError['message']>();

  apiValidationErrors.forEach(item => {
    errorsMap.set(item.line, [...item.message]);
  });

  permissionsValidationsErrors.forEach(item => {
    const currentErrorMessages = errorsMap.get(item.line) ?? [];
    const newErrorMessages = [...currentErrorMessages, ...item.message];

    errorsMap.set(item.line, newErrorMessages);
  });

  return Array.from(errorsMap.entries()).map(([line, message]) => ({ line, message }));
};

export const removeInvalids = <EntityType>(
  shapedLines: WithRowNumber<Partial<EntityType>>[],
  exclusionMap: Map<number, boolean>,
) => shapedLines.filter(({ rowNumber }) => !exclusionMap.has(rowNumber));

/**
 * Sum of all entities to be uploaded: (options.length + offers.length + categories.length) === 100%
 * Then we split proportionally to each entity (not 33% each, it depends on the size of each entity)
 * Then, during upload we can calculate in percentages how much one Offer/Option/Category corresponds to the total and thus update the progress percentage on screen
 */
export const buildPercentages = ({
  offers,
  options,
  categories,
}: {
  offers?: WithRowNumber<Partial<OfferLine>>[];
  options?: WithRowNumber<Partial<OptionLine>>[];
  categories?: WithRowNumber<Partial<CategoryLine>>[];
} = {}): {
  offers: number;
  options: number;
  categories: number;
} => {
  const total = (offers ?? [])?.length + (options ?? [])?.length + (categories ?? [])?.length;
  const offersPercentage = Math.floor(((offers ?? []).length * 100) / total);
  const optionsPercentage = Math.floor(((options ?? []).length * 100) / total);
  const categoriesPercentage = Math.floor(((categories ?? []).length * 100) / total);
  return {
    offers: offersPercentage || 0,
    options: optionsPercentage || 0,
    categories: categoriesPercentage || 0,
  };
};

export const removeNonMatchingKeysFromObject = (object: Record<string, any>, keys?: string[]) =>
  !!keys ? Object.fromEntries(Object.entries(object).filter(([key]) => keys.includes(key))) : object;

export const reorderOffersByCategoriesOrder = (offers: Offer[], categories: SelectionCategory[]): Offer[] => {
  if (!categories?.length) {
    return offers;
  }

  const offerIdsInOrderOfCategoriesMap = flattenCategories(categories)?.reduce<Record<string, Offer>>(
    (acc, flatCategory) => {
      const currentCatOfferIds = flatCategory?.offer_bindings
        ? Object.fromEntries(flatCategory?.offer_bindings?.map(ob => [ob.offer_id, {} as Offer]))
        : {};

      return { ...acc, ...currentCatOfferIds };
    },
    {},
  );

  const offersWithoutCategory: Offer[] = [];
  offers?.forEach(offer => {
    if (offerIdsInOrderOfCategoriesMap[offer.id]) {
      offerIdsInOrderOfCategoriesMap[offer.id] = offer;
      return;
    }

    offersWithoutCategory.push(offer);
  });

  const reorderedOffers: Offer[] = [...Object.values(offerIdsInOrderOfCategoriesMap), ...offersWithoutCategory];

  return reorderedOffers;
};
